import { useState, useEffect, useCallback } from 'react';
import { Sparkles, Send, Trash2, Loader2, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';

const BASE = '/api/telegram/app';

// Telegram WebApp SDK
declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        initData: string;
        initDataUnsafe: { user?: { first_name: string; id: number } };
        ready: () => void;
        close: () => void;
        expand: () => void;
        MainButton: {
          text: string;
          show: () => void;
          hide: () => void;
          onClick: (fn: () => void) => void;
          offClick: (fn: () => void) => void;
          showProgress: (leaveActive?: boolean) => void;
          hideProgress: () => void;
          setParams: (params: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }) => void;
        };
        themeParams: Record<string, string>;
        colorScheme: 'light' | 'dark';
        setHeaderColor: (color: string) => void;
        setBackgroundColor: (color: string) => void;
        BackButton: { show: () => void; hide: () => void; onClick: (fn: () => void) => void };
        HapticFeedback: { impactOccurred: (style: string) => void; notificationOccurred: (type: string) => void };
      };
    };
  }
}

function getTg() {
  return window.Telegram?.WebApp;
}

function getInitData() {
  return getTg()?.initData || '';
}

async function tgFetch(path: string, opts?: RequestInit) {
  const initData = getInitData();
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'X-Telegram-Init-Data': initData,
      ...(opts?.headers || {}),
    },
  });
  return res.json();
}

interface Question {
  id: number;
  text: string;
  text_translated?: string;
  date_created: string;
  item_id: string;
  marketplace_item_id?: string;
  seller_id: number;
  from: { id: number; nickname: string };
  // Enriched flat fields from service
  item_title: string | null;
  item_price: number | null;
  item_net_proceeds: number | null;
  item_currency: string | null;
  item_permalink: string | null;
  item_thumbnail: string | null;
  asin: string | null;
  amazon_cost: number | null;
  amazon_brand: string | null;
  amazon_rating: number | null;
  amazon_features: string[];
  matched_directives: string[];
  buyer_order: {
    ml_order_id: number;
    status: string;
    paid_amount: number;
  } | null;
}

/** Wait for Telegram WebApp SDK to be available (loaded via index.html) */
function waitForTelegramSdk(timeout = 3000): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Telegram?.WebApp?.initData) { resolve(true); return; }
    const start = Date.now();
    const check = () => {
      if (window.Telegram?.WebApp?.initData) { resolve(true); return; }
      if (Date.now() - start > timeout) { resolve(false); return; }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

export default function QuestionMiniApp() {
  const params = new URLSearchParams(window.location.search);
  const questionId = window.location.pathname.match(/\/question\/(\d+)/)?.[1];
  const sellerId = params.get('seller_id') || '1';
  const viewAll = window.location.pathname.includes('/questions') && !questionId;

  const [sdkReady, setSdkReady] = useState(false);
  const [question, setQuestion] = useState<Question | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(questionId || null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Wait for Telegram SDK, then init
  useEffect(() => {
    waitForTelegramSdk().then((ready) => {
      if (ready) {
        const tg = getTg();
        if (tg) {
          tg.ready();
          tg.expand();
          const isDark = tg.colorScheme === 'dark';
          document.body.style.backgroundColor = isDark ? '#1e1e1e' : '#ffffff';
          document.body.style.color = isDark ? '#ffffff' : '#000000';
        }
      }
      setSdkReady(true);
    });
  }, []);

  // Load question(s) — only after SDK is ready
  const load = useCallback(async () => {
    if (!sdkReady) return;
    setLoading(true);
    setError('');
    try {
      if (viewAll || !selectedId) {
        const res = await tgFetch(`/pending?seller_id=${sellerId}`);
        if (res.success) {
          setQuestions(res.data || []);
        } else {
          setError(res.error || 'Error loading questions');
        }
      } else {
        const res = await tgFetch(`/question/${selectedId}?seller_id=${sellerId}`);
        if (res.success) {
          setQuestion(res.data);
        } else {
          setError(res.error || 'Question not found');
        }
      }
    } catch (err) {
      setError('Connection error');
    }
    setLoading(false);
  }, [sdkReady, selectedId, sellerId, viewAll]);

  useEffect(() => { load(); }, [load]);

  // Generate AI draft
  const handleGenerateAI = async () => {
    if (!selectedId) return;
    setGenerating(true);
    setError('');
    try {
      const res = await tgFetch(`/question/${selectedId}/generate-ai?seller_id=${sellerId}`, { method: 'POST' });
      if (res.success && res.data?.ai_draft) {
        setAnswer(res.data.ai_draft);
        getTg()?.HapticFeedback?.notificationOccurred('success');
      } else if (res.error === 'agent_busy') {
        setError('El asistente está generando la respuesta. Esperá unos segundos y reintentá.');
        getTg()?.HapticFeedback?.notificationOccurred('error');
      } else {
        setError(res.error || 'AI generation failed');
        getTg()?.HapticFeedback?.notificationOccurred('error');
      }
    } catch {
      setError('AI generation failed');
    }
    setGenerating(false);
  };

  // Send answer
  const handleAnswer = async () => {
    if (!selectedId || !answer.trim()) return;
    setSending(true);
    setError('');
    try {
      const res = await tgFetch(`/question/${selectedId}/answer?seller_id=${sellerId}`, {
        method: 'POST',
        body: JSON.stringify({ text: answer.trim() }),
      });
      if (res.success) {
        setSent(true);
        getTg()?.HapticFeedback?.notificationOccurred('success');
        // Auto-close after 1.5s
        setTimeout(() => {
          if (viewAll) {
            setSelectedId(null);
            setSent(false);
            setAnswer('');
            setQuestion(null);
            load();
          } else {
            getTg()?.close();
          }
        }, 1500);
      } else {
        setError(res.error || 'Failed to send answer');
        getTg()?.HapticFeedback?.notificationOccurred('error');
      }
    } catch {
      setError('Failed to send answer');
    }
    setSending(false);
  };

  // Delete question
  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await tgFetch(`/question/${selectedId}?seller_id=${sellerId}`, { method: 'DELETE' });
      getTg()?.HapticFeedback?.notificationOccurred('success');
      if (viewAll) {
        setSelectedId(null);
        setQuestion(null);
        load();
      } else {
        getTg()?.close();
      }
    } catch { /* ignore */ }
  };

  const isDark = (getTg()?.colorScheme === 'dark') || window.matchMedia('(prefers-color-scheme: dark)').matches;
  const bg = isDark ? '#1e1e1e' : '#ffffff';
  const cardBg = isDark ? '#2d2d2d' : '#f8f9fa';
  const borderColor = isDark ? '#3d3d3d' : '#e5e7eb';
  const textPrimary = isDark ? '#ffffff' : '#111827';
  const textSecondary = isDark ? '#9ca3af' : '#6b7280';
  const textMuted = isDark ? '#6b7280' : '#9ca3af';
  const accentBlue = '#3b82f6';
  const accentGreen = '#22c55e';
  const accentPurple = '#a855f7';

  // ── Sent confirmation ──
  if (sent) {
    return (
      <div style={{ background: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
        <CheckCircle size={48} color={accentGreen} />
        <p style={{ color: textPrimary, fontSize: 18, fontWeight: 600 }}>Respuesta enviada</p>
      </div>
    );
  }

  // ── Loading ──
  if (loading) {
    return (
      <div style={{ background: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={32} color={accentBlue} style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  // ── Error ──
  if (error && !question && questions.length === 0) {
    return (
      <div style={{ background: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, padding: 20 }}>
        <AlertCircle size={40} color="#ef4444" />
        <p style={{ color: textPrimary, fontSize: 14, textAlign: 'center' }}>{error}</p>
      </div>
    );
  }

  // ── Questions list ──
  if (!selectedId && (viewAll || questions.length > 0)) {
    return (
      <div style={{ background: bg, minHeight: '100vh', padding: 16, fontFamily: '-apple-system, system-ui, sans-serif' }}>
        <h1 style={{ color: textPrimary, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
          Preguntas Pendientes ({questions.length})
        </h1>
        {questions.length === 0 ? (
          <p style={{ color: textSecondary, textAlign: 'center', paddingTop: 40 }}>Sin preguntas pendientes</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {questions.map((q: any) => (
              <div
                key={q.id}
                onClick={() => { setSelectedId(String(q.id)); setQuestion(q); }}
                style={{
                  background: cardBg, borderRadius: 12, padding: 14, cursor: 'pointer',
                  border: `1px solid ${borderColor}`, transition: 'transform 0.1s',
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  {q.item_thumbnail && (
                    <img src={q.item_thumbnail.replace('http://', 'https://')} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'contain', background: '#fff', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ color: textPrimary, fontSize: 14, fontWeight: 500, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {q.item_title || q.asin || `Item ${q.item_id}`}
                    </p>
                    <p style={{ color: textSecondary, fontSize: 13, marginBottom: 2 }}>
                      <i>"{((q as any).text_translated || q.text || '').substring(0, 80)}{((q as any).text_translated || q.text || '').length > 80 ? '...' : ''}"</i>
                    </p>
                    <p style={{ color: textMuted, fontSize: 11 }}>
                      {q.from?.nickname || 'Comprador'} · {timeAgo(q.date_created)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Single question detail ──
  const q = question;
  if (!q) return null;

  return (
    <div style={{ background: bg, minHeight: '100vh', fontFamily: '-apple-system, system-ui, sans-serif' }}>
      {/* Back button for list view */}
      {viewAll && (
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${borderColor}` }}>
          <button onClick={() => { setSelectedId(null); setQuestion(null); setAnswer(''); setError(''); }}
            style={{ background: 'none', border: 'none', color: accentBlue, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <ArrowLeft size={16} /> Volver
          </button>
        </div>
      )}

      {/* Product card */}
      <div style={{ padding: 16, borderBottom: `1px solid ${borderColor}` }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {q.item_thumbnail && (
            <img src={q.item_thumbnail.replace('http://', 'https://')} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'contain', background: '#fff', flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: textPrimary, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              {q.item_title || q.asin || `Item ${q.item_id}`}
            </p>
            {q.asin && (
              <span style={{ display: 'inline-block', background: isDark ? '#134e4a' : '#ccfbf1', color: isDark ? '#5eead4' : '#0f766e', fontSize: 11, padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontWeight: 600 }}>
                {q.asin}
              </span>
            )}
            {q.item_price && (
              <span style={{ color: accentGreen, fontSize: 13, fontWeight: 600, marginLeft: 8 }}>
                ${q.item_price} {q.item_currency}
              </span>
            )}
            {q.amazon_cost && (
              <span style={{ color: textSecondary, fontSize: 11, marginLeft: 6 }}>
                (AMZ: ${q.amazon_cost})
              </span>
            )}
          </div>
        </div>

        {/* Amazon specs */}
        {q.amazon_brand && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: textSecondary }}>Brand: {q.amazon_brand}</span>
            {q.amazon_rating && <span style={{ fontSize: 11, color: '#eab308' }}>★ {q.amazon_rating}</span>}
          </div>
        )}
        {q.amazon_features && q.amazon_features.length > 0 && (
          <div style={{ marginTop: 6 }}>
            {q.amazon_features.slice(0, 2).map((f, i) => (
              <p key={i} style={{ fontSize: 11, color: textMuted, marginBottom: 2 }}>• {f.substring(0, 100)}</p>
            ))}
          </div>
        )}
      </div>

      {/* Question */}
      <div style={{ padding: 16, borderBottom: `1px solid ${borderColor}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: textSecondary, fontWeight: 500 }}>
            👤 {q.from?.nickname || 'Comprador'}
          </span>
          <span style={{ fontSize: 11, color: textMuted }}>{timeAgo(q.date_created)}</span>
        </div>
        <p style={{ color: textPrimary, fontSize: 16, lineHeight: 1.5 }}>
          "{(q as any).text_translated || q.text}"
        </p>

        {/* Buyer order badge */}
        {q.buyer_order && (
          <div style={{ marginTop: 8, background: isDark ? '#2d1f0f' : '#fef3c7', padding: '6px 10px', borderRadius: 6, fontSize: 11 }}>
            <span style={{ color: isDark ? '#fbbf24' : '#92400e' }}>
              Orden #{q.buyer_order.ml_order_id} · {q.buyer_order.status} · ${q.buyer_order.paid_amount}
            </span>
          </div>
        )}

        {/* Directives */}
        {q.matched_directives && q.matched_directives.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {q.matched_directives.map((d, i) => (
              <span key={i} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: isDark ? '#3b1f7a' : '#ede9fe', color: accentPurple, fontWeight: 500 }}>
                {d}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Answer area */}
      <div style={{ padding: 16 }}>
        {error && (
          <div style={{ background: isDark ? '#450a0a' : '#fef2f2', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#ef4444' }}>
            {error}
          </div>
        )}

        <textarea
          value={answer}
          onChange={e => setAnswer(e.target.value)}
          placeholder="Escribir respuesta..."
          style={{
            width: '100%', minHeight: 100, padding: 12, borderRadius: 10,
            border: `1px solid ${borderColor}`, background: cardBg,
            color: textPrimary, fontSize: 14, resize: 'vertical',
            fontFamily: '-apple-system, system-ui, sans-serif',
            outline: 'none', boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span style={{ fontSize: 11, color: answer.length > 1900 ? '#ef4444' : textMuted }}>
            {answer.length}/2000
          </span>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {/* Generate AI */}
          <button
            onClick={handleGenerateAI}
            disabled={generating}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 10, border: 'none',
              background: isDark ? '#3b1f7a' : '#ede9fe', color: accentPurple,
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              opacity: generating ? 0.6 : 1,
            }}
          >
            {generating ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={16} />}
            AI
          </button>

          {/* Delete */}
          <button
            onClick={handleDelete}
            style={{
              padding: '12px 16px', borderRadius: 10, border: 'none',
              background: isDark ? '#450a0a' : '#fef2f2', color: '#ef4444',
              fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Trash2 size={16} />
          </button>

          {/* Send */}
          <button
            onClick={handleAnswer}
            disabled={sending || !answer.trim()}
            style={{
              flex: 2, padding: '12px 16px', borderRadius: 10, border: 'none',
              background: accentBlue, color: '#fff',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              opacity: (sending || !answer.trim()) ? 0.5 : 1,
            }}
          >
            {sending ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
            Responder
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
